import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { usePosterGenerator } from './hooks/usePosterGenerator';
import { trackShare } from '../../services/legendApi';
import PosterPreview from './PosterPreview';
import TemplateSelector from './TemplateSelector';
import ShareButtons from './ShareButtons';
import styles from './Share.module.css';

/**
 * 分享模态框主组件
 * 整合海报生成、模板选择和分享功能
 *
 * 支持三种模式：
 * 1. 配置分享 (configId + configData) - 配置库/详情页分享
 * 2. 传奇分享 (legendId) - 铸造传奇后的分享
 * 3. 配置器分享 (无 configId) - 当前配置器的配置分享
 */
export default function ShareModal({
    isOpen,
    onClose,
    configId = null,
    // 配置数据（用于配置库/详情页分享）
    configData = null,        // 配置详情对象 { body, trim, comfort, ... }
    coverImageUrl = null,     // 配置封面图片
    totalPrice = null,        // 配置总价
    configTitle = null,       // 配置标题
    // 传奇分享相关
    legendId = null,
    legendTitle = null,
    legendImage = null,
}) {
    const { user } = useAuth();
    const { language } = useLanguage();
    const posterRef = useRef(null);

    // 状态
    const [selectedTemplate, setSelectedTemplate] = useState('wild');
    const [isReady, setIsReady] = useState(false);
    const [imageReady, setImageReady] = useState(false);

    // 海报生成 Hook
    const {
        isGenerating,
        posterDataUrl,
        qrCodeDataUrl,
        error,
        generateShareLink,
        generateQRCode,
        generatePoster,
        downloadPoster,
        copyShareLink,
        shareToSocial,
        reset
    } = usePosterGenerator();

    // 判断是否为传奇分享模式
    const isLegendShare = !!legendId;

    // 生成分享链接
    const shareLink = isLegendShare
        ? `${window.location.origin}/legend/${legendId}`
        : generateShareLink(user?.id, configId);

    // 判断是否有外部图片需要转换
    const hasExternalImage = !!(coverImageUrl || legendImage);

    // 处理图片准备完成回调
    const handleImageReady = useCallback((success) => {
        console.log('[ShareModal] Image ready, success:', success);
        setImageReady(true);
    }, []);

    // 组件挂载时生成二维码
    useEffect(() => {
        if (isOpen && shareLink) {
            generateQRCode(shareLink).then(() => {
                // 如果没有外部图片，直接设置 ready
                if (!hasExternalImage) {
                    setTimeout(() => setIsReady(true), 500);
                }
                // 如果有外部图片，等待 imageReady 回调
            });
        }

        return () => {
            if (!isOpen) {
                reset();
                setIsReady(false);
                setImageReady(false);
            }
        };
    }, [isOpen, shareLink, generateQRCode, reset, hasExternalImage]);

    // 当图片准备好时设置 ready
    useEffect(() => {
        if (hasExternalImage && imageReady && qrCodeDataUrl) {
            console.log('[ShareModal] External image ready, setting isReady');
            setIsReady(true);
        }
    }, [hasExternalImage, imageReady, qrCodeDataUrl]);

    // 模板切换时重新生成海报
    useEffect(() => {
        if (isReady && posterRef.current && qrCodeDataUrl) {
            // 延迟生成，等待模板渲染和图片加载完成
            // 外部图片需要更长的等待时间
            const waitTime = hasExternalImage ? 800 : 300;
            const timer = setTimeout(() => {
                generatePoster(posterRef.current);
            }, waitTime);
            return () => clearTimeout(timer);
        }
    }, [selectedTemplate, isReady, qrCodeDataUrl, generatePoster, hasExternalImage]);

    // 下载海报
    const handleDownload = useCallback(async () => {
        if (posterDataUrl) {
            const timestamp = new Date().toISOString().slice(0, 10);
            const filename = isLegendShare
                ? `fatboy-legend-${timestamp}.png`
                : `fatboy-config-${timestamp}.png`;
            downloadPoster(posterDataUrl, filename);

            // 如果是传奇分享，记录分享
            if (isLegendShare && legendId) {
                try {
                    await trackShare(legendId, 'download');
                } catch (e) {
                    console.error('Failed to track share:', e);
                }
            }
        }
    }, [posterDataUrl, downloadPoster, isLegendShare, legendId]);

    // 复制链接
    const handleCopyLink = useCallback(async () => {
        const result = await copyShareLink(shareLink);

        // 如果是传奇分享，记录分享
        if (isLegendShare && legendId && result) {
            try {
                await trackShare(legendId, 'copy_link');
            } catch (e) {
                console.error('Failed to track share:', e);
            }
        }

        return result;
    }, [copyShareLink, shareLink, isLegendShare, legendId]);

    // 分享到社交媒体
    const handleShare = useCallback(async () => {
        // 将海报转换为 Blob
        let files = [];
        const filename = isLegendShare ? 'fatboy-legend.png' : 'fatboy-config.png';

        if (posterDataUrl) {
            try {
                const response = await fetch(posterDataUrl);
                const blob = await response.blob();
                files = [new File([blob], filename, { type: 'image/png' })];
            } catch (e) {
                console.error('Failed to create file from poster:', e);
            }
        }

        // 根据分享类型设置标题和描述
        const legendTitleLabel = { zh: '我的传奇', en: 'My Legend', de: 'Meine Legende' };
        const configTitleLabel = { zh: '我的哈雷 Fat Boy 配置', en: 'My Harley Fat Boy Configuration', de: 'Meine Harley Fat Boy Konfiguration' };
        const legendTextLabel = {
            zh: '我铸造了一台传奇！来Fat Boy Configurator看看我的改装作品，向我的传奇致敬！',
            en: 'I forged a legend! Check out my custom build on Fat Boy Configurator and salute my legend!',
            de: 'Ich habe eine Legende geschmiedet! Schau dir meinen Custom Build auf Fat Boy Configurator an und grüße meine Legende!'
        };
        const configTextLabel = {
            zh: '看看我定制的2026款哈雷 Fat Boy！',
            en: 'Check out my custom 2026 Harley Fat Boy!',
            de: 'Schau dir meinen individuellen 2026 Harley Fat Boy an!'
        };

        const shareTitle = isLegendShare
            ? `🔥 ${legendTitle || legendTitleLabel[language] || legendTitleLabel.en} 🔥`
            : (configTitleLabel[language] || configTitleLabel.en);

        const shareText = isLegendShare
            ? (legendTextLabel[language] || legendTextLabel.en)
            : (configTextLabel[language] || configTextLabel.en);

        await shareToSocial({
            title: shareTitle,
            text: shareText,
            url: shareLink,
            files: files.length > 0 ? files : undefined
        });

        // 如果是传奇分享，记录分享
        if (isLegendShare && legendId) {
            try {
                await trackShare(legendId, 'native_share');
            } catch (e) {
                console.error('Failed to track share:', e);
            }
        }
    }, [posterDataUrl, shareLink, shareToSocial, language, isLegendShare, legendId, legendTitle]);

    // 关闭模态框
    const handleClose = useCallback(() => {
        reset();
        setIsReady(false);
        setSelectedTemplate('wild');
        onClose();
    }, [onClose, reset]);

    // 点击遮罩关闭
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    }, [handleClose]);

    // ESC 键关闭
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, handleClose]);

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                {/* 头部 */}
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        {isLegendShare
                            ? `🔥 ${{ zh: '分享我的传奇', en: 'Share My Legend', de: 'Meine Legende teilen' }[language] || 'Share My Legend'}`
                            : ({ zh: '分享我的配置', en: 'Share My Config', de: 'Meine Konfiguration teilen' }[language] || 'Share My Config')
                        }
                    </h3>
                    <button className={styles.closeBtn} onClick={handleClose}>
                        ×
                    </button>
                </div>

                {/* 主体 */}
                <div className={styles.modalBody}>
                    {/* 错误提示 */}
                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    {/* 海报预览 */}
                    <div className={styles.posterWrapper}>
                        <PosterPreview
                            ref={posterRef}
                            template={selectedTemplate}
                            qrCodeUrl={qrCodeDataUrl}
                            shareLink={shareLink}
                            // 外部传入的配置数据（用于配置库/详情页分享）
                            externalConfigData={configData}
                            externalCoverImage={coverImageUrl}
                            externalTotalPrice={totalPrice}
                            externalConfigTitle={configTitle}
                            // 传奇分享数据
                            isLegendShare={isLegendShare}
                            legendTitle={legendTitle}
                            legendImage={legendImage}
                            // 图片准备完成回调
                            onImageReady={handleImageReady}
                        />
                        {/* 生成中遮罩 */}
                        {isGenerating && (
                            <div className={styles.generatingOverlay}>
                                <div className={styles.spinner} />
                                <span className={styles.generatingText}>
                                    {{ zh: '生成中...', en: 'Generating...', de: 'Generieren...' }[language] || 'Generating...'}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* 模板选择器 */}
                    <TemplateSelector
                        selectedTemplate={selectedTemplate}
                        onSelect={setSelectedTemplate}
                    />

                    {/* 分享按钮 */}
                    <ShareButtons
                        onDownload={handleDownload}
                        onCopyLink={handleCopyLink}
                        onShare={handleShare}
                        isGenerating={isGenerating}
                        posterReady={!!posterDataUrl}
                    />
                </div>
            </div>
        </div>
    );
}
